import type { CvContentBlock } from '@/units/ds/utils/cv-content-block.type'
import { CV_ENTRY_GAP } from '@/units/ds/utils/cv-pagination.constants'

/**
 * Cuts a splittable section so that as many entries as possible fit into
 * `remaining` px, and returns the rest as a `-cont` block for the next page.
 *
 * `fit` is null when not even the section header plus its first entry fit —
 * the caller is expected to start a new page and try again. `overflow` is null
 * when the whole section fits.
 */
export function splitCvBlock(
  block: CvContentBlock,
  remaining: number,
): { fit: CvContentBlock | null; overflow: CvContentBlock | null } {
  const entryHeights = block.entryHeights
  const headerHeight = block.headerHeight ?? 0

  if (!entryHeights || entryHeights.length === 0) {
    return { fit: null, overflow: block }
  }

  if (remaining < headerHeight + entryHeights[0]) {
    return { fit: null, overflow: block }
  }

  let used = headerHeight
  let fitCount = 0
  for (let i = 0; i < entryHeights.length; i++) {
    const gap = i > 0 ? CV_ENTRY_GAP : 0
    if (used + gap + entryHeights[i] > remaining) break
    used += gap + entryHeights[i]
    fitCount++
  }

  if (fitCount === 0) return { fit: null, overflow: block }
  if (fitCount === block.entries.length) return { fit: block, overflow: null }

  const baseType = block.type.replace('-cont', '')
  const overflowEntryHeights = entryHeights.slice(fitCount)
  const overflowHeight =
    overflowEntryHeights.reduce((sum, h) => sum + h, 0) +
    Math.max(0, overflowEntryHeights.length - 1) * CV_ENTRY_GAP

  return {
    fit: {
      type: block.type,
      entries: block.entries.slice(0, fitCount),
      height: used,
      entryHeights: entryHeights.slice(0, fitCount),
      headerHeight,
    },
    overflow: {
      type: `${baseType}-cont`,
      entries: block.entries.slice(fitCount),
      height: overflowHeight,
      entryHeights: overflowEntryHeights,
      headerHeight: 0,
    },
  }
}
